import React, { FunctionComponent, useContext, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'
import { Popover } from 'react-native-popper'

import { format } from 'date-fns'
import { isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CalendarAlert, CalendarAlertType } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { DropdownButton } from '../../atoms/dropdownButton/DropdownButton'
import { ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { ChevronV2Icon } from '../../atoms/icons/ChevronV2Icon'
import { GearIcon } from '../../atoms/icons/GearIcon'
import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { QuestionMarkIcon } from '../../atoms/icons/QuestionMarkIcon'
import { XIcon } from '../../atoms/icons/XIcon'
import { LoadingSkeleton } from '../../atoms/loadingSkeleton/LoadingSkeleton'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Title } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { AppContext } from '../../context'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { ProviderCalendarMetricBadge } from '../providerCalendarMetricBadge/ProviderCalendarMetricBadge'

export type ProviderCalendarHeaderProps = {
  onAddSlotPressed: () => void
  onAddSessionPressed: () => void
  onNextPressed: () => void
  onPrevPressed: () => void
  onTodayPressed: () => void
  onSettingsPressed: () => void
  onOpenZendeskPressed: () => void
  rangeText: string
  scheduledSessions: number | null
  clientHours: number | null
  alerts: CalendarAlert[]
  onAlertPressed: (alert: CalendarAlert) => void
  hidePerformanceMetrics?: boolean
  hybridBookingMode?: boolean
  showSessionSubmission?: boolean
  sessionsAwaitingSubmission?: number | null
  rightHeaderSection?: React.ReactNode
}

const Header = styled.View(({ theme }) => ({
  flexDirection: 'column',
  position: 'sticky',
  top: 0,
  backgroundColor: theme.colors.backgroundPrimary,
  zIndex: 3,
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const HeaderContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing['16px'],
  padding: `${theme.spacing['16px']} ${theme.spacing['24px']}`,
  backgroundColor: theme.colors.backgroundPrimary,
}))

const HeaderInnerContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['16px'],
}))

const MetricsContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const Metric = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['12px'],
  padding: `6px ${theme.spacing['8px']}`,
  border: `1px solid ${theme.colors.borderDefault}`,
  borderRadius: '8px',
}))

const IconButtonContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const IconButton = styled(TertiaryIconButton)(({ theme }) => ({
  alignSelf: 'center',
  border: `1.5px solid ${theme.colors.borderDefault}`,
  height: '48px',
  width: '48px',
}))

const CenteredIconButton = styled(TertiaryIconButton)({
  alignSelf: 'center',
})

const PopoverContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: '16px',
  padding: theme.spacing['16px'],
  gap: theme.spacing['16px'],
  boxShadow: `0 2px 10px ${theme.colors.shadowLow}`,
}))

const PopoverRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['48px'],
}))

const CloseButton = styled(PressableOpacity)({
  alignSelf: 'flex-end',
})

const TodayButton = styled(TertiaryButton)(({ theme }) => ({
  alignSelf: 'center',
  borderColor: theme.colors.borderDefault,
  borderWidth: '1.5px',
}))

export const ProviderCalendarHeader: FunctionComponent<ProviderCalendarHeaderProps> = ({
  onAddSlotPressed,
  onAddSessionPressed,
  onNextPressed,
  onPrevPressed,
  onTodayPressed,
  onSettingsPressed,
  onOpenZendeskPressed,
  rangeText,
  scheduledSessions,
  clientHours,
  alerts,
  onAlertPressed,
  hidePerformanceMetrics,
  hybridBookingMode,
  showSessionSubmission,
  sessionsAwaitingSubmission = 0,
  rightHeaderSection,
}) => {
  const theme = useTheme() as ThemeType
  const { isInProductCalendarAddModalEnabled, showCalendarScheduleSession, showLegacyScheduleSession } =
    useContext(AppContext)
  const { showIPCSessionCreate } = useFlags()
  const [isOpen, setIsOpen] = useState(false)
  const { formatMessage } = useIntl()
  const pressableRef = useRef(null)
  const bookableAlert = useMemo(() => alerts.find((alert) => alert.type === CalendarAlertType.BOOKABLE), [alerts])
  const scheduledAlert = useMemo(() => alerts.find((alert) => alert.type === CalendarAlertType.SCHEDULED), [alerts])
  return (
    <Header>
      <HeaderContainer>
        <HeaderInnerContainer>
          <TodayButton
            aria-label={formatMessage(
              {
                defaultMessage: 'Today {actualDate}',
                description: 'Button text that moves calendar to today',
              },
              { actualDate: format(new Date(), 'PPPP') },
            )}
            customTextColor={theme.colors.textSecondary}
            testID={tID('ProviderCalendarHeader-today-button')}
            text={<FormattedMessage defaultMessage='Today' description='Button text that moves calendar to today' />}
            onPress={onTodayPressed}
          />
          <IconButtonContainer>
            <IconButton
              aria-label={formatMessage({
                defaultMessage: 'Previous week',
                description: 'Button that moves calendar to previous week',
              })}
              leftIcon={<ChevronV2Icon direction={ChevronIconDirection.LEFT} />}
              onPress={onPrevPressed}
              testID={tID('ProviderCalendarHeader-previous-button')}
            />
            <IconButton
              aria-label={formatMessage({
                defaultMessage: 'Next week',
                description: 'Button that moves calendar to next week',
              })}
              testID={tID('ProviderCalendarHeader-next-button')}
              leftIcon={<ChevronV2Icon direction={ChevronIconDirection.RIGHT} />}
              onPress={onNextPressed}
            />
          </IconButtonContainer>
          <Title
            aria-label={rangeText}
            tabIndex={0}
            text={rangeText}
            size={TitleSize.LARGE}
            testID={tID('ProviderCalendarHeader-range-text')}
          />
        </HeaderInnerContainer>
        <HeaderInnerContainer>
          {!hidePerformanceMetrics && (
            <MetricsContainer testID={tID('ProviderCalendarHeader-metrics-client-hours')}>
              <Metric>
                <Title
                  aria-label={formatMessage(
                    {
                      defaultMessage: '{numberOfSpots} calendar spots',
                      description: 'Button text of the month and year the calendar is presenting right now',
                    },
                    { numberOfSpots: clientHours },
                  )}
                  size={TitleSize.LARGE}
                  text={clientHours}
                  tabIndex={0}
                />
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage='Calendar spots'
                      description='Label for calendar spots slots on lc calendar header'
                    />
                  }
                />
                {bookableAlert && (
                  <View role='alert'>
                    <ProviderCalendarMetricBadge
                      testID={tID('ProviderCalendarMetricBadge-bookable-badge')}
                      alert={bookableAlert}
                      onPress={() => onAlertPressed(bookableAlert)}
                    />
                  </View>
                )}
              </Metric>
              <Metric testID={tID('ProviderCalendarHeader-metrics-scheduled')}>
                <Title
                  aria-label={formatMessage(
                    {
                      defaultMessage: '{scheduledSessions} scheduled sessions',
                      description: 'Text for the number of scheduled sessions the Provider has',
                    },
                    { scheduledSessions },
                  )}
                  size={TitleSize.LARGE}
                  text={scheduledSessions}
                  tabIndex={0}
                />
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage='Scheduled'
                      description='Label for scheduled sessions on lc calendar header'
                    />
                  }
                />
                {scheduledAlert && (
                  <View role='alert'>
                    <ProviderCalendarMetricBadge
                      testID={tID('ProviderCalendarMetricBadge-scheduled-badge')}
                      alert={scheduledAlert}
                      onPress={() => onAlertPressed(scheduledAlert)}
                    />
                  </View>
                )}
              </Metric>
            </MetricsContainer>
          )}
          {showSessionSubmission && (
            <MetricsContainer testID={tID('ProviderCalendarHeader-metrics-session-submission')}>
              <Metric>
                {isNil(sessionsAwaitingSubmission) ? (
                  <LoadingSkeleton width={24} height={24} marginTop={6} marginBottom={6} />
                ) : (
                  <Title
                    aria-label={formatMessage(
                      {
                        defaultMessage:
                          '{sessionsAwaitingSubmission, plural, one {1 session} other {{sessionsAwaitingSubmission} sessions}} awaiting submission',
                        description: 'Text for the number of sessions awaiting submission',
                      },
                      { sessionsAwaitingSubmission },
                    )}
                    size={TitleSize.LARGE}
                    text={sessionsAwaitingSubmission}
                    tabIndex={0}
                  />
                )}
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage='{sessionsAwaitingSubmission, plural, one {Session} other {Sessions}} awaiting submission'
                      description='Label for sessions awaiting submission on calendar header'
                      values={{ sessionsAwaitingSubmission }}
                    />
                  }
                />
                <Tooltip
                  content={
                    <FormattedMessage
                      defaultMessage='Submit invoice and note for payment'
                      description='Tooltip text for sessions awaiting submission metric'
                    />
                  }
                  hoverEnabled
                >
                  <InfoIcon size={24} />
                </Tooltip>
              </Metric>
            </MetricsContainer>
          )}
          {rightHeaderSection}
          {isInProductCalendarAddModalEnabled &&
            (showIPCSessionCreate && (showCalendarScheduleSession || showLegacyScheduleSession) ? (
              <DropdownButton
                customTextColor={theme.colors.textActive}
                dropdownWidth={240}
                testID={tID('ProviderCalendarHeader-add-dropdown')}
                dropdownItems={[
                  {
                    text: showLegacyScheduleSession
                      ? formatMessage({
                          defaultMessage: 'Schedule follow-up session',
                          description: 'Button to schedule a follow-up session for legacy providers',
                        })
                      : formatMessage({
                          defaultMessage: 'Schedule session',
                          description: 'Button to schedule a session to the calendar',
                        }),
                    id: 'schedule-session',
                    testId: tID('ProviderCalendarHeader-add-session-button'),
                    selectHandler: onAddSessionPressed,
                  },
                  {
                    text: formatMessage({
                      defaultMessage: 'Add one-time availability',
                      description: 'Button to add availability to the calendar',
                    }),
                    id: 'add-availability',
                    testId: tID('ProviderCalendarHeader-add-availability-button'),
                    selectHandler: onAddSlotPressed,
                  },
                ]}
                customButtonText={formatMessage({
                  defaultMessage: 'Add',
                  description: 'Button to add availability to the calendar',
                })}
                disableSelectedValueHighlight
              />
            ) : (
              <SecondaryButton
                testID={tID('ProviderBookableCalendarSetup-add-button')}
                text={
                  <FormattedMessage defaultMessage='Add' description='Button to add availability to calendar setup' />
                }
                onPress={onAddSlotPressed}
              />
            ))}
          <CenteredIconButton
            aria-label={formatMessage({
              defaultMessage: 'Settings',
              description: 'Button text of the Settings button',
            })}
            testID={tID('ProviderCalendarHeader-settings-button')}
            leftIcon={<GearIcon color={theme.colors.iconDefault} size={20} isFilled={false} />}
            onPress={onSettingsPressed}
          />
          <View ref={pressableRef}>
            <CenteredIconButton
              aria-label={formatMessage({
                defaultMessage: 'Zendesk',
                description: 'Button text of the Zendesk help button',
              })}
              testID={tID('ProviderCalendarHeader-help-button')}
              leftIcon={<QuestionMarkIcon fillColor={theme.colors.iconDefault} width={20} />}
              onPress={() => setIsOpen(true)}
            />
          </View>
          <Popover isOpen={isOpen} onOpenChange={setIsOpen} trigger={pressableRef} placement='left top'>
            <Popover.Backdrop />
            <Popover.Content>
              <PopoverContainer>
                <PopoverRow>
                  <Title
                    tabIndex={0}
                    size={TitleSize.SMALL}
                    text={
                      <FormattedMessage
                        defaultMessage='Need help with your Lyra calendar?'
                        description='Header for provider calendar help popover'
                      />
                    }
                  />
                  <CloseButton
                    aria-label={formatMessage({
                      defaultMessage: 'Close dialog',
                      description: 'Button text to close the Zendesk dialog',
                    })}
                    hitSlop={60}
                    onPress={() => setIsOpen(false)}
                  >
                    <XIcon size={24} fillColor={theme.colors.iconDefault} />
                  </CloseButton>
                </PopoverRow>
                <SecondaryButton
                  testID={tID('ProviderCalendarHeader-open-zendesk-button')}
                  onPress={onOpenZendeskPressed}
                  text={
                    hybridBookingMode ? (
                      <FormattedMessage
                        defaultMessage='Open Help Center'
                        description='Button to get help with calendar'
                      />
                    ) : (
                      <FormattedMessage defaultMessage='Open Zendesk' description='Button to get help with calendar' />
                    )
                  }
                />
              </PopoverContainer>
            </Popover.Content>
          </Popover>
        </HeaderInnerContainer>
      </HeaderContainer>
    </Header>
  )
}
